// Timeline visualization utilities for consolidating date columns
import { formatDateSmart } from './dateFormatting';

/**
 * Creates a visual timeline string using characters w, U, A, m, _, O, X
 * @param {Object} params - Timeline parameters
 * @param {Date|string} params.creationDate - Date the item was created
 * @param {Date|string} params.lastUpdatedDate - Date of most recent update
 * @param {boolean} params.hasOpenAction - Whether there is an open action
 * @param {string} params.status - Current status
 * @param {Date} params.today - Current date (defaults to new Date())
 * @returns {string} Timeline visualization string
 */
export function createTimelineString({
  creationDate,
  lastUpdatedDate,
  hasOpenAction = false,
  status,
  today = new Date()
}) {
  // Parse dates
  const creation = creationDate ? new Date(creationDate) : null;
  const lastUpdate = lastUpdatedDate ? new Date(lastUpdatedDate) : null;
  
  if (!creation || isNaN(creation.getTime())) {
    return '_X'; // Invalid creation date
  }

  // Calculate weeks between creation and today
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const weeksSinceCreation = Math.floor((today - creation) / msPerWeek);
  
  if (weeksSinceCreation < 0) {
    return '_O'; // Future date
  }

  // Step 3: Initialize with 'w' for each week
  let timeline = Array(weeksSinceCreation).fill('w');

  // Step 4: Mark last update with 'U'
  if (lastUpdate && !isNaN(lastUpdate.getTime()) && lastUpdate >= creation && lastUpdate <= today) {
    const weeksToUpdate = Math.floor((lastUpdate - creation) / msPerWeek);
    if (weeksToUpdate >= 0 && weeksToUpdate < timeline.length) {
      timeline[weeksToUpdate] = 'U';
    }
  }

  // Step 5: Insert open action marker 'A'
  if (hasOpenAction) {
    // Place 'A' after the last significant event or at the end
    const lastEventWeek = lastUpdate ? Math.floor((lastUpdate - creation) / msPerWeek) : -1;
    const insertPosition = Math.min(Math.max(lastEventWeek, timeline.length - 1), timeline.length - 1);
    
    if (insertPosition >= 0 && insertPosition < timeline.length) {
      // Insert A after the character at this position
      const beforeA = timeline.slice(0, insertPosition + 1);
      const afterA = timeline.slice(insertPosition + 1);
      timeline = [...beforeA, 'A', ...afterA];
    } else {
      timeline.push('A');
    }
  }

  // Step 6: Add today marker '_'
  timeline.push('_');

  // Step 8: Append status marker (O or X)
  const openStatuses = ['New', 'Attempt to Contact', 'Engaged', 'Discovery', 'Proposal', 'Refinement', 'Contract Sent'];
  const statusMarker = openStatuses.includes(status) ? 'O' : 'X';
  timeline.push(statusMarker);

  // Compress 'w' runs to 'm' if record is over 3 months old
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const isOldRecord = creation < threeMonthsAgo;
  
  let processedTimeline = timeline;
  
  if (isOldRecord) {
    // Compress consecutive 'w' runs into 'm' for old records
    const compressed = [];
    let i = 0;
    while (i < timeline.length - 2) { // Don't compress the final _O or _X
      if (timeline[i] === 'w') {
        let runLength = 0;
        let j = i;
        while (j < timeline.length - 2 && timeline[j] === 'w') {
          runLength++;
          j++;
        }
        
        if (runLength >= 4) {
          compressed.push('m');
        } else {
          compressed.push(...timeline.slice(i, j));
        }
        i = j;
      } else {
        compressed.push(timeline[i]);
        i++;
      }
    }
    
    // Add the final _O or _X
    compressed.push(...timeline.slice(-2));
    processedTimeline = compressed;
  }
  
  // Show only trailing 15 characters
  const timelineString = processedTimeline.join('');
  const maxLength = 15;
  
  if (timelineString.length <= maxLength) {
    return timelineString;
  }
  
  // Return only the trailing 15 characters
  return timelineString.slice(-maxLength);
}

/**
 * Creates detailed tooltip text for timeline visualization
 */
export function createTimelineTooltip({
  creationDate,
  lastUpdatedDate, 
  hasOpenAction,
  status,
  followUpDate
}) {
  const lines = [];
  
  // Creation date
  if (creationDate) {
    lines.push(`Created: ${formatDateSmart(creationDate)}`);
  }
  
  // Last updated
  if (lastUpdatedDate) {
    lines.push(`Last Updated: ${formatDateSmart(lastUpdatedDate)}`);
  } else {
    lines.push('Last Updated: Never');
  }
  
  // Open action status
  if (hasOpenAction && followUpDate) {
    lines.push(`Next Action: ${formatDateSmart(followUpDate)}`);
  } else if (hasOpenAction) {
    lines.push('Open Action: Yes');
  } else {
    lines.push('Open Action: No');
  }
  
  // Status
  lines.push(`Status: ${status}`);
  
  // Legend
  lines.push('');
  lines.push('Legend:');
  lines.push('w = week, U = update, A = action');
  lines.push('m = multiple weeks, _ = today');
  lines.push('O = open, X = closed');
  
  return lines.join('\n');
}

/**
 * Determines if an association has open actions based on followUp date and lastActivity
 */
export function hasOpenActions(association) {
  // Check for future followUp dates
  const followUp = association.followUp;
  if (followUp) {
    const followUpDate = new Date(followUp);
    const today = new Date();
    
    // Has open action if followUp is in the future
    if (followUpDate > today) {
      return true;
    }
  }
  
  // Check for recent activity (within last 7 days) as indicator of ongoing action
  const lastActivity = association.lastActivity;
  if (lastActivity) {
    const activityDate = new Date(lastActivity);
    const today = new Date();
    const daysDiff = (today - activityDate) / (1000 * 60 * 60 * 24);
    
    // Recent activity suggests ongoing engagement
    if (daysDiff <= 7) {
      return true;
    }
  }
  
  return false;
}